import React, { useMemo } from "react";
import { Card, Col, Placeholder } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import { useFetchNotesQuery } from "features/notes/noteSlice";
import { useFetchClassesQuery } from "features/classes/classeSlice";
import { useGetNiveauxQuery } from "features/niveaux/niveauxSlice";

const NotesChart = () => {
  const { data: notes = [], isLoading: loadNotes, isError } = useFetchNotesQuery();
  const { data: classes = [], isLoading: loadClasses } = useFetchClassesQuery();
  const { data: niveaux = [], isLoading: loadNiveaux } = useGetNiveauxQuery();

  const isLoading = loadNotes || loadClasses || loadNiveaux;

  const { categories, series } = useMemo(() => {
    const niveauMap: Record<string, string> = {};
    niveaux.forEach((n: any) => {
      if (n._id) niveauMap[n._id] = n.nom_niveau;
    });

    const classeToNiveau: Record<string, string> = {};
    classes.forEach((c: any) => {
      if (c._id && c.niveau) {
        classeToNiveau[c._id] = niveauMap[c.niveau] || c.niveau;
      }
    });

    const niveauNotes: Record<string, number[]> = {};

    notes.forEach((note) => {
      const niveauName = classeToNiveau[note.classe] || "Autre";
      if (!niveauNotes[niveauName]) niveauNotes[niveauName] = [];

      if (note.eleves && Array.isArray(note.eleves)) {
        note.eleves.forEach((e: any) => {
          const val = parseFloat(e.note ?? e.valeur ?? e.moyenne);
          if (!isNaN(val)) niveauNotes[niveauName].push(val);
        });
      }
    });

    const cats: string[] = [];
    const avgData: number[] = [];
    const countData: number[] = [];

    Object.entries(niveauNotes)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([name, vals]) => {
        cats.push(name);
        const avg = vals.length > 0
          ? Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 100) / 100
          : 0;
        avgData.push(avg);
        countData.push(vals.length);
      });

    return {
      categories: cats,
      series: [
        { name: "Moyenne", data: avgData },
      ],
    };
  }, [notes, classes, niveaux]);

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      height: 300,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "50%",
        distributed: true,
      },
    },
    colors: [
      "#3498db", "#2ecc71", "#e67e22", "#9b59b6",
      "#1abc9c", "#e74c3c", "#f39c12", "#34495e",
    ],
    dataLabels: {
      enabled: true,
      formatter: (val: number) => val.toFixed(1),
      style: { fontSize: "12px" },
    },
    legend: { show: false },
    xaxis: {
      categories,
      labels: {
        rotate: -45,
        style: { fontSize: "12px" },
      },
    },
    yaxis: {
      title: { text: "Moyenne /20" },
      min: 0,
      max: 20,
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 4,
    },
    tooltip: {
      y: { formatter: (val: number) => `${val.toFixed(2)} /20` },
    },
  };

  return (
    <Col xxl={6} lg={12}>
      <Card>
        <Card.Header>
          <h5 className="card-title mb-0">
            <i className="ri-bar-chart-2-line me-2 text-primary" />
            Moyenne des notes par niveau
          </h5>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <Placeholder as="div" animation="glow">
              <Placeholder xs={12} style={{ height: 300 }} />
            </Placeholder>
          ) : isError ? (
            <div className="text-center text-danger py-5">
              <i className="ri-error-warning-line fs-1 d-block mb-2" />
              Impossible de charger les données
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="ri-bar-chart-line fs-1 d-block mb-2" />
              Aucune note enregistrée
            </div>
          ) : (
            <ReactApexChart
              options={options}
              series={series}
              type="bar"
              height={300}
            />
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default NotesChart;
